import React, { createContext, useContext, useState } from "react";
import {
  Button,
  Typography,
  TextField,
  Stack,
  Paper,
  CircularProgress,
} from "@mui/material";
import { Formik } from "formik";

const AuthContextContext = createContext();

export function useAuthContext() {
  return useContext(AuthContextContext);
}

export function AuthContextProvider({ children }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [token, setToken] = useState(localStorage.getItem("authToken") || "");
  const authenticated = token && token.length > 0;
  const updateToken = (newToken) => {
    setToken(newToken);
    localStorage.setItem("authToken", newToken);
  };

  const authenticate = (name, password) => {
    setLoading(true);
    setError("");
    if (process.env.NODE_ENV === "development") {
      updateToken("dev");
      setLoading(false);
      return;
    }
    fetch("/authentication/authenticate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, password }),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Error authenticating");
        return res.json();
      })
      .then((data) => {
        updateToken(`Bearer ${data.token}`);
      })
      .catch((e) => {
        setError(e.message || "Authentication failed");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const logout = () => {
    updateToken("");
  };

  return (
    <AuthContextContext.Provider
      value={{ token, authenticated, authenticate, loading, error, logout }}
    >
      {children}
    </AuthContextContext.Provider>
  );
}

export function AuthContextForm({ children }) {
  const { authenticate, authenticated, loading, error } = useAuthContext();

  if (authenticated) {
    return children;
  }

  return (
    <Paper sx={{ margin: "auto", padding: 4, maxWidth: 500, mt: 8 }}>
      <Formik
        initialValues={{ name: "", code: "" }}
        validate={({ name, code }) => {
          const errors = {};
          if (!name) errors.name = "Please enter your name";
          if (!code) errors.code = "Please enter a code";
          return errors;
        }}
        onSubmit={async (values, { setSubmitting }) => {
          await authenticate(values.name, values.code);
          setSubmitting(false);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
        }) => (
          <Stack spacing={2}>
            <Typography variant="h4" color="secondary.main" align="center">
              Secret Page
            </Typography>
            <TextField
              label="Enter Name"
              variant="outlined"
              name="name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.name && Boolean(errors.name)}
              helperText={touched.name && errors.name}
              disabled={loading}
            />
            <TextField
              label="Enter Access Code"
              variant="outlined"
              name="code"
              value={values.code}
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
              type="password"
              color="secondary"
              error={touched.code && Boolean(errors.code)}
              helperText={touched.code && errors.code}
              disabled={loading}
            />
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {loading ? <CircularProgress size={24} /> : "Enter"}
            </Button>
            {error && <Typography color="error.main">{error}</Typography>}
          </Stack>
        )}
      </Formik>
    </Paper>
  );
}
